import { useState } from 'react';
import { Pressable, useColorScheme, type PressableProps } from 'react-native';
import { type TextProps } from 'tamagui';

import { Text } from '@/components/text';
import { darkColors, lightColors, radius, space } from '@/theme/tokens.generated';
import { hit, slopFor, TARGET } from '@/lib/hit-slop';

// Issue #148 AC2. Grepped every real Pressable-wrapped text link across
// the app (~31 call sites: Block/Report, Sign out, "Get the Verified
// badge", radio options, age-gate, LocationCard's "Try again"/"Update")
// -- every single one used bare `accessibilityRole="button"` +
// `hitSlop` + `onPress` with NO visual hover/press/focus treatment at
// all, the exact gap this AC names. BackLink (#150) already solved this
// for the specific Back/Cancel/Close role; this is the same recipe
// generalized for every OTHER text-link role that isn't "dismiss this
// screen."
//
// Real outline on focus, not a Tamagui `outlineColor` theme reference --
// this wraps a plain react-native `Pressable`, not a Tamagui-styled
// component, so there's no theme-aware style resolution happening
// automatically the way `color="$colorLink"` on a Tamagui `Text` gets;
// `useColorScheme()` + the same light/dark `actionPrimary` hex pair
// PrimaryButton's own `$outlineColor` theme entry uses (tamagui.config.
// shared.ts, issue #148 AC1) keeps this visually IDENTICAL to that
// button's own focus ring rather than inventing a second value, using
// the same pattern this codebase already reaches for elsewhere (native
// RefreshControl tintColor) for a native semantic color outside
// Tamagui's own theme-prop system.
// Hover swaps to `$colorLinkHover` (a real, already-existing theme
// token -- confirmed via tokens.generated.ts's own textLink/
// textLinkHover pair) rather than just relying on press/focus alone,
// since mouse users get no other affordance that this text is
// interactive without it. Press: opacity 0.7, the simplest, most
// universal "this is being tapped" signal, matching this component's
// intentionally lighter-weight role next to PrimaryButton/Card's own
// scale+shadow treatment (a text link isn't a raised surface).
export interface TextLinkProps extends Omit<TextProps, 'onPress'> {
  onPress: () => void;
  hitSlop?: PressableProps['hitSlop'];
  disabled?: boolean;
}

export function TextLink({ children, onPress, hitSlop, disabled, ...textProps }: TextLinkProps) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const colorScheme = useColorScheme();
  const outlineColor = colorScheme === 'dark' ? darkColors.actionPrimary : lightColors.actionPrimary;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ disabled: !!disabled }}
      aria-disabled={!!disabled}
      disabled={disabled}
      {...(hitSlop === undefined ? hit(slopFor(36)) : { hitSlop })}
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={({ pressed }) => [
        { paddingVertical: space[2], minWidth: TARGET, borderRadius: radius.sm },
        focused ? { outlineStyle: 'solid', outlineWidth: 2, outlineColor, outlineOffset: 2 } : null,
        pressed ? { opacity: 0.7 } : null,
        disabled ? { opacity: 0.5 } : null,
      ]}
    >
      {/* fontFamily="$body" default -- founder, 2026-09-16, adversarial-
          lens pass: caught two individual bare-Text instances rendering
          in the wrong font (preview.tsx's footer, threads.tsx's radius
          line), both already documented as symptoms of the same known
          gap: bare `<Text>` has no font-family default of its own,
          unlike Paragraph/Heading (built on SizableText, which DOES
          default to "$body"). TextLink is the highest-leverage instance
          of that gap in the whole app -- this component's own doc
          comment above counts ~31 real call sites (Block/Report, Sign
          out, every "Try again" retry, "View their profile before
          deciding", etc.), every one of them silently rendering in the
          OS system font instead of Libre Franklin until now. Set BEFORE
          the `{...textProps}` spread so a caller that ever needs a
          different family can still override it. */}
      <Text fontFamily="$body" color={hovered ? '$colorLinkHover' : '$colorLink'} {...textProps}>
        {children}
      </Text>
    </Pressable>
  );
}
